import { useNavigate } from "react-router-dom";
import { ArrowRight, AlertCircle, CheckCircle2, Copy, FileText, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import Markdown from "@/components/Markdown";
import { Skeleton } from "@/components/Skeleton";
import { ARTIFACT_DEFS } from "./constants";

export function TFAEmpty() {
  return (
    <div className="tb-card p-8 sm:p-12 text-center" data-testid="tfa-empty">
      <div className="mx-auto h-16 w-16 rounded-full tb-ai-orb grid place-items-center tb-float mb-4">
        <Sparkles className="h-7 w-7 text-white" />
      </div>
      <h3 className="font-display text-xl tracking-tight font-medium">Ready when you are</h3>
      <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
        Add a document or topic on the left, choose which artifacts you want, then hit Generate. TeeBee will produce all of them in parallel — usually in under a minute.
      </p>
      <div className="mt-6 flex flex-wrap gap-2 justify-center">
        {ARTIFACT_DEFS.slice(0, 4).map((a) => {
          const Icon = a.icon;
          return (
            <span key={a.key} className="tb-action-chip">
              <span className={`inline-grid place-items-center h-5 w-5 rounded-full ${a.chip}`}>
                <Icon className="h-3 w-3" />
              </span>
              {a.label}
            </span>
          );
        })}
      </div>
    </div>
  );
}

export function TFARunningSkeleton({ extracting, selectedCount, selectedKeys }) {
  return (
    <div className="tb-card p-6 tb-tracing-border" data-testid="tfa-running">
      <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-primary inline-flex items-center gap-2">
        <Sparkles className="h-3 w-3" /> TeeBee is generating
      </p>
      <h3 className="mt-2 font-display text-xl tracking-tight font-medium">
        {extracting ? "Reading your document" : `Building ${selectedCount} artifacts in parallel`}
      </h3>
      <p className="mt-1 text-xs text-muted-foreground">
        {extracting ? "Parsing layout, math and language…" : "Lesson plan, quiz, worksheet and more — coming up."}
      </p>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {(selectedKeys.length ? selectedKeys : ["slot-0","slot-1","slot-2","slot-3"]).map((k) => (
          <div key={k} className="rounded-xl border border-border/60 p-3 bg-card/60">
            <Skeleton className="h-3 w-1/3 mb-2" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-5/6 mt-2" />
            <Skeleton className="h-3 w-4/6 mt-2" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function TFAResult({ result, activeTab, setActiveTab, onCopy }) {
  const navigate = useNavigate();
  return (
    <>
      <div className="tb-card p-5 tb-fade-up" data-testid="tfa-result-header">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <span className="tb-chip-mint rounded-full text-[11px] px-2.5 py-1 font-medium inline-flex items-center gap-1.5">
              <CheckCircle2 className="h-3 w-3" /> Generated in {Math.round(result.duration_ms / 1000)}s
            </span>
            <h3 className="mt-2 font-display text-lg tracking-tight font-medium truncate">{result.source_title}</h3>
            <p className="text-xs text-muted-foreground">
              {result.subject} · Grade {result.grade} · {result.language}
            </p>
          </div>
          <div className="flex gap-2 items-center">
            {result.lesson_id && (
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => navigate(`/lesson/${result.lesson_id}`)} data-testid="tfa-open-lesson">
                Open Lesson <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="tb-card p-5 sm:p-6 tb-fade-up" data-testid="tfa-result-canvas">
        <div className="flex flex-wrap gap-2 border-b border-border pb-3 mb-4 overflow-x-auto tb-no-scrollbar">
          {result.artifacts.map((a) => {
            const def = ARTIFACT_DEFS.find((d) => d.key === a.kind);
            const Icon = def?.icon || FileText;
            const active = activeTab === a.kind;
            return (
              <button
                key={a.kind}
                onClick={() => setActiveTab(a.kind)}
                className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition ${
                  active ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-muted/80 text-foreground"
                } ${a.error ? "opacity-50" : ""}`}
                data-testid={`tfa-tab-${a.kind}`}
              >
                <Icon className="h-3.5 w-3.5" />
                {def?.label || a.kind}
                {a.error && <AlertCircle className="h-3 w-3" />}
              </button>
            );
          })}
        </div>

        {activeTab && (() => {
          const a = result.artifacts.find((x) => x.kind === activeTab);
          if (!a) return null;
          if (a.error) {
            return (
              <div className="rounded-xl bg-destructive/10 text-destructive p-4 text-sm">
                <p className="font-medium">Could not generate this artifact</p>
                <p className="mt-1 text-xs opacity-80">{a.error}</p>
              </div>
            );
          }
          return (
            <div className="tb-fade-up">
              <div className="flex items-center justify-end mb-3">
                <Button onClick={onCopy} size="sm" variant="outline" className="rounded-full" data-testid="tfa-copy">
                  <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy
                </Button>
              </div>
              <Markdown>{a.content}</Markdown>
            </div>
          );
        })()}
      </div>
    </>
  );
}
